import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { media } from '../styles/breakpoints';
import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Highlight,
    FeatureItem as OriginalFeatureItem,
    FeatureTitle,
    FeaturesGrid,
    HeroSection as BaseHeroSection,
    FigureWrapper,
    FigureCaption,
    Placeholder,
} from '../components/ui/PageElements';

import {
    AudienceItem,
    AudienceList,
    FeatureList,
    FeatureListItem,
    TechSpecsList,
    TechSpecItem
} from "../components/ui/ListStyles.jsx";
import ResponsiveImage from '../components/ui/ResponsiveImage';
import PageMeta from '../components/PageMeta';
import WhatsNewSection from '../components/ui/WhatsNewSection';
import { email } from '../utils/generalData';

const Logo = styled.img`
    max-width: 233px;
    width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
`;

const HeroSection = styled(BaseHeroSection)``;

const SubHeadlineContainer = styled.div`
    font-size: 18px;
    max-width: 700px;
    margin: 0 auto 40px;
    color: var(--text-muted);
    line-height: 1.6;

    p {
        margin-bottom: 20px;
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const FigureImage = styled(ResponsiveImage)`
    display: block;
    margin: 0 auto;
    border: none;
    background-color: transparent;
    max-height: 250px;
    object-fit: contain;

    picture, img {
        display: block;
        margin: 0 auto;
        border: none;
        background-color: transparent;
        max-height: 250px;
        object-fit: contain;
    }
`;

const LargeFigureImage = styled(FigureImage)`
    max-height: 400px;

    img {
        max-height: 400px;
    }
`;

const ProjectWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;

const FeatureItem = styled(OriginalFeatureItem)`
    cursor: pointer;
    transition: border-color 0.3s ease, background-color 0.3s ease;
    border-color: ${({ $isActive }) => ($isActive ? 'var(--primary-color)' : 'inherit')};
    background-color: ${({ $isActive }) => ($isActive ? 'rgba(255, 51, 153, 0.08)' : 'rgba(15, 15, 25, 0.5)')};
    position: relative;
    margin-bottom: ${({ $isActive }) => ($isActive ? '0' : '8px')};
    border-radius: 0;
    font: inherit;
    text-align: left;
    width: 100%;

    .feature-list-item {
        list-style: none;
        &::before {
            display: none;
        }
    }
`;

const FeatureContentLayout = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
    margin-bottom: 20px;
    overflow: hidden;

    ${media.sm} {
        display: block;
    }
`;

const ThumbnailWrapper = styled.div`
    flex: 1 1 200px;
    display: flex;
    justify-content: center;
    min-width: 120px;
    max-width: ${props => props.$fullWidth ? '100%' : '300px'};

    ${media.sm} {
        float: left;
        margin-right: 12px;
        margin-bottom: 8px;
        min-width: auto;
        max-width: none;
    }
`;

const FeatureThumbnail = styled(ResponsiveImage)`
    width: ${props => props.$fullWidth ? 'auto' : '120px'};
    height: 120px;
    object-fit: ${props => props.$fullWidth ? 'contain' : 'cover'};
    border: none;
    background-color: transparent;
    max-width: ${props => props.$fullWidth ? '100%' : '120px'};

    img {
        width: ${props => props.$fullWidth ? 'auto' : '120px'};
        height: 120px;
        object-fit: ${props => props.$fullWidth ? 'contain' : 'cover'};
        border: none;
        background-color: transparent;
        max-width: ${props => props.$fullWidth ? '100%' : '120px'};
    }

    ${media.sm} {
        width: auto;
        height: 80px;
        max-width: none;

        img {
            width: auto;
            height: 80px;
            max-width: none;
        }
    }
`;

const FeatureTextContent = styled.div`
    flex: 999 1 auto;
    min-width: 200px;
    overflow-wrap: break-word;
    word-wrap: break-word;

    ul {
        margin-top: 0;
    }

    ${media.sm} {
        min-width: 0;
        width: 100%;
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    .fixed-button {
        min-width: 200px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px;
        padding: 0 20px !important;
        box-sizing: border-box;
    }
`;

const AccordionSection = styled.div`
    position: relative;
    margin-bottom: ${({ $isExpanded, $isLast }) => ($isExpanded ? '0' : $isLast ? '0' : '8px')};
`;

const StickyTitleContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(10, 10, 20, 0.98);
    backdrop-filter: blur(15px);
    border-bottom: 2px solid var(--primary-color);
    padding: 15px 40px;
    margin: 0;
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--primary-color);
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    margin-left: -41px;
    margin-right: -41px;
    width: calc(100% + 82px);
`;

const DetailsContainer = styled.div`
    max-height: ${({ height }) => height === 0 ? '0px' : '10000px'};
    overflow: clip;
    transition: max-height 300ms cubic-bezier(0.4, 0, 0.2, 1);
    background-color: ${({ height }) => height > 0 ? 'rgba(15, 15, 25, 0.8)' : 'transparent'};
    border: ${({ height }) => height > 0 ? '1px solid rgba(255, 51, 153, 0.2)' : 'none'};
    margin-bottom: ${({ height, $isLast }) => (height > 0 && !$isLast ? '8px' : '0')};
    position: relative;
`;

const ContentWrapper = styled.div`
    padding: 30px 40px;
    opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
    transform: translateY(${({ isVisible }) => (isVisible ? '0' : '-10px')});
    transition: opacity 300ms cubic-bezier(0.4, 0, 0.2, 1),
                transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 400px;
`;

const SummaryButton = styled(StyledButton)`
    margin-top: auto;
    padding-top: 15px;
    padding-bottom: 15px;
    width: 100%;
`;

const ExpandableGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 40px;
`;

const ExportTargetsDetails = styled.details`
    margin: 20px 0;
    border: 1px solid rgba(255, 51, 153, 0.25);
    background-color: rgba(15, 15, 25, 0.5);

    summary {
        padding: 12px 18px;
        cursor: pointer;
        font-weight: 600;
        color: var(--primary-color);
        list-style: none;
        user-select: none;

        &::-webkit-details-marker { display: none; }
        &::before {
            content: '▸';
            display: inline-block;
            margin-right: 10px;
            transition: transform 0.2s ease;
        }
    }

    &[open] summary::before {
        transform: rotate(90deg);
    }
`;

const ExportTargetsList = styled.ul`
    list-style: none;
    padding: 0 18px 18px;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 6px 18px;

    li {
        padding: 4px 0;
        color: var(--text-color);
        font-size: 14px;

        &::before {
            content: '·';
            color: var(--primary-color);
            margin-right: 8px;
        }
    }
`;

const parseDescription = (text) => {
    const parts = text.split(/(<Highlight>.*?<\/Highlight>)/);
    return parts.map((part, index) => {
        if (part.startsWith('<Highlight>') && part.endsWith('</Highlight>')) {
            const content = part.replace(/<\/?Highlight>/g, '');
            return <Highlight key={index}>{content}</Highlight>;
        }
        return part;
    });
};

const sectionOrder = ['analysis', 'language', 'environment'];

const SectionContent = ({ sectionName }) => {
    const DetailFeatureItem = OriginalFeatureItem;

    switch(sectionName) {
        case 'analysis':
            return (
                <FeaturesGrid>
                    <DetailFeatureItem>
                        <FeatureTitle>Complete Oscilloscope</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Time-domain analysis</Highlight> rendered at up to 60 fps. Multi-channel support with distinct colors per output.</FeatureListItem>
                            <FeatureListItem><Highlight>Advanced Triggering:</Highlight> Free, Auto, or Single trigger modes with rising/falling edge and level selection to lock onto periodic signals.</FeatureListItem>
                            <FeatureListItem><Highlight>Data Export:</Highlight> Export the captured time window to CSV for deeper offline analysis and verification.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <FigureImage src="/assets/FaustStudio/img/oscilloscope.png" alt="View of the oscilloscope in trigger mode, with input signal on channel 1 and reverb output on channel 2." sizes="(max-width: 800px) 100vw, 800px" />
                            <FigureCaption>
                                The Oscilloscope in trigger mode, displaying the input signal on Channel 1 and the reverb output on Channel 2.
                            </FigureCaption>
                        </FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Spectral Visualization</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Spectroscope:</Highlight> Instantaneous frequency spectrum rendered with an adaptive hue-sweep LUT for clear amplitude reading.</FeatureListItem>
                            <FeatureListItem><Highlight>Spectrogram:</Highlight> Scrolling time-frequency heat map. The content history is preserved perfectly across panel resizes and tab switches.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <FigureImage src="/assets/FaustStudio/img/spectroscope.png" alt="View of the spectroscope showing frequency analysis." sizes="(max-width: 800px) 100vw, 800px" />
                            <FigureCaption>
                                Real-time spectral visualization with the built-in Spectroscope.
                            </FigureCaption>
                        </FigureWrapper>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Offline Analysis Bench</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Measure, don't just watch:</Highlight> A dedicated tool window renders a test signal through your DSP offline — no audio playback — to plot its frequency, impulse, step, THD, and static transfer responses.</FeatureListItem>
                            <FeatureListItem><Highlight>Point and analyze:</Highlight> Trigger from a gutter icon on <code>process</code> or any definition, or by selecting an expression. Only the analyses valid for that target's arity are offered.</FeatureListItem>
                            <FeatureListItem><Highlight>Compare and keep:</Highlight> Overlay multiple runs to compare designs ("did my edit flatten the passband?"), export plots to PNG/CSV, and save analysis sessions that persist across IDE restarts.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <FigureImage src="/assets/FaustStudio/img/staticAnalysis.png" alt="View of the analysis bench with 3 plots of frequency response" sizes="(max-width: 800px) 100vw, 800px" />
                            <FigureCaption>
                                The Faust Analysis Bench overlaying three filter frequency responses.
                            </FigureCaption>
                        </FigureWrapper>
                    </DetailFeatureItem>
                </FeaturesGrid>
            );
        case 'language':
            return (
                <FeaturesGrid>
                    <DetailFeatureItem>
                        <FeatureTitle>Deep Language Understanding</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Intelligent Code Completion:</Highlight> Context-aware completion for keywords, built-in functions, UI constructors, standard library prefixes, and user-defined symbols.</FeatureListItem>
                            <FeatureListItem><Highlight>Real-Time Diagnostics:</Highlight> Static-analysis findings (unresolved references, arity mismatches, unused parameters) and Faust compiler errors both surface as inline editor annotations, with click-through into <code>.lib</code> files at the offending line.</FeatureListItem>
                            <FeatureListItem><Highlight>Arity Inlay Hints:</Highlight> Instantly see signal arity (e.g., `n→m`) next to definitions. The custom inference engine understands cross-definition references, partial application, and the Faust Macro Heuristic.</FeatureListItem>
                            <FeatureListItem><Highlight>Signature Help:</Highlight> As you type a function call, see its parameter names with the current argument highlighted — for your own functions and standard-library functions alike (e.g. <code>fi.lowpass(N, fc, x)</code>).</FeatureListItem>
                            <FeatureListItem><Highlight>Named-Argument Hints:</Highlight> Inlay hints render each parameter name before its argument at the call site (e.g. <code>fi.lowpass(N: 3, fc: 1000)</code>), so positional arguments are never a guessing game.</FeatureListItem>
                        </FeatureList>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Developer Productivity</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Stub-Based Indexing:</Highlight> Instant cross-file resolution. Go-to-definition, find usages, and rename refactoring work seamlessly across your entire project.</FeatureListItem>
                            <FeatureListItem><Highlight>Instant Documentation:</Highlight> Hover or press Ctrl+Q to view function signatures, preceding comments, and full documentation for built-in primitives without leaving your code.</FeatureListItem>
                            <FeatureListItem><Highlight>Structure View & Navigation:</Highlight> Quickly navigate complex DSP files with a detailed file outline (Alt+7) showing functions, their parameter lists, and previews.</FeatureListItem>
                            <FeatureListItem><Highlight>Quick-Fixes & Intentions:</Highlight> Press Alt+Enter on a problem for one-click fixes — "Did you mean…?" typo suggestions, add a missing <code>process</code>, insert <code>import("stdfaust.lib")</code>, or remove an unused parameter.</FeatureListItem>
                        </FeatureList>
                    </DetailFeatureItem>
                </FeaturesGrid>
            );
        case 'environment':
            return (
                <FeaturesGrid>
                    <DetailFeatureItem>
                        <FeatureTitle>Zero-Setup Audio Engine</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Bundled Compiler:</Highlight> Compiles Faust DSP to WebAssembly inside an embedded Chromium browser. No local Faust installation required to start coding.</FeatureListItem>
                            <FeatureListItem><Highlight>Optional Native Path:</Highlight> If you have Faust installed locally, the plugin uses it as the primary compiler — useful for very large or complex DSPs that exceed the in-browser compiler's limits. The bundled WASM compiler remains available as a fallback.</FeatureListItem>
                            <FeatureListItem><Highlight>Soundfile Support:</Highlight> DSPs that use the <code>soundfile(...)</code> primitive can load real audio assets from disk. The plugin's embedded HTTP server resolves relative URLs against the <code>.dsp</code> file's parent directory and serves the assets to the webview — fully path-traversal-safe.</FeatureListItem>
                            <FeatureListItem><Highlight>Lock-Free Capture:</Highlight> PCM capture happens via AudioWorklet and SharedArrayBuffer, decoupling audio generation from visual rendering entirely.</FeatureListItem>
                        </FeatureList>
                    </DetailFeatureItem>
                    <DetailFeatureItem>
                        <FeatureTitle>Interactive Prototyping</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Auto-Generated UI:</Highlight> Tweak your DSP parameters on the fly using interactive sliders, buttons, and checkboxes generated directly from your code.</FeatureListItem>
                            <FeatureListItem><Highlight>MIDI Integration:</Highlight> Declare polyphony and play your synths using the built-in virtual piano keyboard or by connecting your own hardware MIDI controller.</FeatureListItem>
                            <FeatureListItem><Highlight>Audio Routing:</Highlight> Test your DSP live using your system microphone, a built-in 440 Hz sine wave, or a white noise generator.</FeatureListItem>
                            <FeatureListItem><Highlight>Run Configurations:</Highlight> Right-click any <code>.dsp</code> file to launch a Run Configuration. Per-run library paths layer on top of project and system-wide settings, so multi-file DSPs and shared <code>.lib</code> trees resolve the way you expect.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <FigureImage src="/assets/FaustStudio/img/gui.png" alt="Auto-generated GUI for a synth and reverb prototype with the virtual keyboard, allowing immediate tweaking and playing." sizes="(max-width: 800px) 100vw, 800px" />
                            <FigureCaption>
                                Auto-generated GUI for a synth and reverb prototype. As soon as the DSP is written, you can immediately tweak the controls and play the virtual keyboard.
                            </FigureCaption>
                        </FigureWrapper>
                    </DetailFeatureItem>
                </FeaturesGrid>
            );
        default:
            return null;
    }
}

const FaustStudioPage = () => {
    const [activeSections, setActiveSections] = useState(new Set());

    const handleToggle = (sectionName) => {
        setActiveSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionName)) {
                newSet.delete(sectionName);
            } else {
                newSet.add(sectionName);
            }
            return newSet;
        });
    };

    return (
        <ProjectWrapper>
            <PageMeta
                title="Faust Studio Plugin"
                description="A JetBrains plugin providing a zero-latency feedback loop for Faust DSP development. Real-time visualization, an offline analysis bench, instant compilation, and advanced code insights."
                canonicalPath="/projects/faust-studio"
            />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Faust Studio",
                "description": "A JetBrains plugin that brings real-time DSP audio visualization, MIDI playback, and an interactive virtual keyboard into your IDE. Compile Faust code in-browser or via a local Faust installation, and immediately hear, visualize, and interact with the result.",
                "applicationCategory": "DeveloperApplication",
                "applicationSubCategory": "Audio DSP Development",
                "operatingSystem": "Windows, macOS, Linux",
                "url": "https://lperron.com/projects/faust-studio",
                "image": "https://lperron.com/assets/FaustStudio/img/hero.png",
                "softwareRequirements": "Any JetBrains IDE 2023.3+",
                "author": {
                    "@type": "Person",
                    "name": "Lucas Perron",
                    "url": "https://lperron.com"
                },
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "EUR"
                }
            })}} />

            <HeroSection>
                <Logo src="/assets/FaustStudio/img/faust_studio_logo.svg" alt="Faust Studio" />
                <SubHeadline>
                    Transform your JetBrains IDE into a real-time audio development environment. Write Faust DSP code and immediately hear and visualize the results—no external tools, no context switching.
                </SubHeadline>
                <FigureWrapper style={{ marginTop: '10px', marginBottom: '30px' }}>
                    <ResponsiveImage
                        src="/assets/FaustStudio/img/hero.png"
                        alt="Full view of a JetBrains IDE with the Faust Studio plugin open during prototyping of a future instrument. Shows project structure, arity hints, oscilloscope visualization, control GUI, and the virtual keyboard."
                        loading="eager"
                        fetchPriority="high"
                        sizes="(max-width: 600px) 90vw, 675px"
                        style={{ maxHeight: '400px', objectFit: 'contain', display: 'block' }}
                    />
                    <FigureCaption>
                        Full view of a JetBrains IDE with the Faust Studio plugin. See project structure, arity hints, oscilloscope visualization, control GUI, and virtual keyboard—all in one place during prototyping.
                    </FigureCaption>
                </FigureWrapper>

                <ButtonWrapper>
                    <StyledButton
                        className="fixed-button"
                        onClick={() => window.open('https://plugins.jetbrains.com/plugin/32628', '_blank', 'noopener,noreferrer')}
                    >
                        Get on JetBrains Marketplace
                    </StyledButton>
                </ButtonWrapper>
            </HeroSection>

            <WhatsNewSection
                intro="Recent additions to the plugin:"
                changelogUrl="/assets/FaustStudio/CHANGELOG.md"
            />

            <Section>
                <SectionTitle>The Immediate Feedback Loop</SectionTitle>
                <SubHeadlineContainer style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    <p>Traditional DSP development is full of friction: write code, run an external compiler, launch a testing tool, analyze the output, repeat. Faust Studio <Highlight>removes the barrier</Highlight> between writing code and analyzing the signal.</p>
                    <p>As you type, the plugin understands your architecture. When the file autosaves, the DSP is instantly compiled to WebAssembly and routed to your speakers. The IDE is no longer just a text editor; it is a <Highlight>live, interactive testbench</Highlight> for signal processing.</p>
                </SubHeadlineContainer>
            </Section>

            <Section>
                <SectionTitle>Core Capabilities</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto' }}>
                    Language intelligence combined with high-performance audio routing. Everything you need to prototype, test, and verify DSP algorithms in one window.
                </SubHeadline>

                <ExpandableGrid>
                    {sectionOrder.map((sectionName, index) => {
                        const isActive = activeSections.has(sectionName);
                        const isLast = index === sectionOrder.length - 1;

                        const getSectionData = (name) => {
                            switch(name) {
                                case 'analysis':
                                    return {
                                        title: 'Signal Analysis',
                                        description: 'Don\'t just listen; <Highlight>verify</Highlight>. A high-performance visualization suite built directly into the tool window: Oscilloscope with advanced triggering, scrolling Spectrogram, and frequency Spectroscope, with <Highlight>CSV export</Highlight> of the capture buffer. A separate <Highlight>Analysis Bench</Highlight> renders test signals offline to plot frequency, impulse, and THD responses — no playback required.',
                                        buttonText: isActive ? 'Hide Analysis Tools' : 'Explore Signal Analysis',
                                        image: '/assets/FaustStudio/img/analysis.png',
                                        imageAlt: 'Image of the plugin, with the oscilloscope at the top, and spectrogram at the bottom.'
                                    };
                                case 'language':
                                    return {
                                        title: 'Language Intelligence',
                                        description: 'A premium coding experience for Faust. Enjoy <Highlight>intelligent code completion</Highlight>, <Highlight>signature help</Highlight>, and <Highlight>instant documentation</Highlight> without leaving your editor. <Highlight>Stub-based indexing</Highlight> provides instant cross-file resolution and rename refactoring, <Highlight>Arity Inlay Hints</Highlight> visualize signal flow dynamically, and <Highlight>Alt+Enter quick-fixes</Highlight> resolve typos and missing imports in one keystroke.',
                                        buttonText: isActive ? 'Hide Language Features' : 'Explore Language Intelligence',
                                        image: '/assets/FaustStudio/img/faust_logo.svg',
                                        imageAlt: 'Faust Logo'
                                    };
                                case 'environment':
                                    return {
                                        title: 'Interactive Environment',
                                        description: 'A <Highlight>zero-setup testing ground</Highlight>. The bundled WASM compiler and Web Audio integration mean you don\'t need a local Faust installation to start prototyping — and if you do have one, the plugin uses it automatically for the most complex DSPs. Auto-generated UI controls and a virtual MIDI keyboard let you interact with your code instantly.',
                                        buttonText: isActive ? 'Hide Environment Details' : 'Explore the Environment',
                                        image: '/assets/FaustStudio/img/gui.png',
                                        imageAlt: 'Auto-generated GUI for a synth and reverb prototype with the virtual keyboard, allowing immediate tweaking and playing.'
                                    };
                                default:
                                    return { title: '', description: '', buttonText: '', image: '' };
                            }
                        };

                        const sectionData = getSectionData(sectionName);

                        return (
                            <AccordionSection key={sectionName} $isExpanded={isActive} $isLast={isLast}>
                                <FeatureItem
                                    as="button"
                                    type="button"
                                    $isActive={isActive}
                                    onClick={() => handleToggle(sectionName)}
                                    aria-expanded={isActive}
                                    aria-controls={`section-details-${sectionName}`}
                                    data-feature-item
                                >
                                    <FeatureTitle>{sectionData.title}</FeatureTitle>
                                    <FeatureContentLayout>
                                        {sectionData.image && (
                                            <ThumbnailWrapper $fullWidth={false}>
                                                <FeatureThumbnail
                                                    src={sectionData.image}
                                                    alt={sectionData.imageAlt || sectionData.title}
                                                    $fullWidth={false}
                                                    sizes="120px"
                                                    preventNavigation={true}
                                                />
                                            </ThumbnailWrapper>
                                        )}
                                        <FeatureTextContent>
                                            <FeatureList>
                                                <FeatureListItem className="feature-list-item">
                                                    {parseDescription(sectionData.description)}
                                                </FeatureListItem>
                                            </FeatureList>
                                        </FeatureTextContent>
                                    </FeatureContentLayout>
                                    <SummaryButton as="div">
                                        {sectionData.buttonText}
                                    </SummaryButton>
                                </FeatureItem>

                                <DetailsContainer
                                    id={`section-details-${sectionName}`}
                                    height={isActive ? 1 : 0}
                                    $isLast={isLast}
                                    data-details-container
                                >
                                    {isActive && (
                                        <div>
                                            <StickyTitleContainer>{sectionData.title}</StickyTitleContainer>
                                            <ContentWrapper isVisible={isActive}>
                                                <SectionContent sectionName={sectionName} />
                                            </ContentWrapper>
                                        </div>
                                    )}
                                </DetailsContainer>
                            </AccordionSection>
                        );
                    })}
                </ExpandableGrid>
            </Section>

            <Section>
                <SectionTitle>Seamless Exporting</SectionTitle>
                <SubHeadlineContainer style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'left' }}>
                    <p>The plugin is an ideal prototyping environment, but Faust is also designed for deployment. When your algorithm is ready, use the built-in export panel to compile your DSP to <Highlight>C++, Rust, WebAssembly, JAX</Highlight>, and over a dozen other targets.</p>
                    <ExportTargetsDetails>
                        <summary>See the full list of export targets</summary>
                        <ExportTargetsList>
                            <li>C++</li>
                            <li>C</li>
                            <li>C++ (old)</li>
                            <li>JUCE plugin (C++)</li>
                            <li>JUCE standalone (C++)</li>
                            <li>C#</li>
                            <li>Rust</li>
                            <li>D</li>
                            <li>Java</li>
                            <li>Julia</li>
                            <li>JAX (Python)</li>
                            <li>JSFX</li>
                            <li>Cmajor</li>
                            <li>Codebox (RNBO)</li>
                            <li>LLVM IR</li>
                            <li>Interpreter bytecode</li>
                            <li>FIR</li>
                            <li>WebAssembly</li>
                            <li>WebAssembly text (WAST)</li>
                            <li>VHDL</li>
                            <li>SDF3</li>
                            <li>ASC</li>
                        </ExportTargetsList>
                    </ExportTargetsDetails>
                    <p style={{ fontSize: '14px' }}><em>Note: exporting requires a standard local Faust installation to be configured in the plugin settings. When configured, the same installation is also used automatically for live preview compilation.</em></p>
                </SubHeadlineContainer>
            </Section>

            <Section>
                <SectionTitle>Motivation</SectionTitle>
                <SubHeadlineContainer style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'left' }}>
                    <p>I built this tool to solve my own workflow frustrations. Visual audio environments like Max or Pure Data can quickly turn into spaghetti code, while most text-based languages feel uninspiring for instrument design. Faust is a beautiful, immediate language with a functional paradigm perfectly suited for audio, but its standard web IDE didn't fit my workflow.</p>
                    <p>I needed a way to write or generate Faust code and test it immediately in my primary IDE. Because this plugin was built to answer my specific needs, it may lack features that are essential to your workflow. If so, please do not hesitate to <a href={`mailto:${email}?subject=Faust Studio Inquiry`} style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}>contact me</a>.</p>
                </SubHeadlineContainer>
            </Section>

            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>Who is Faust Studio For?</SectionTitle>
                <AudienceList style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <AudienceItem><strong>Instrument and plugin designers (<b>me</b>):</strong> Sketch synths and effects with immediate feedback, then export to C++ or Rust when the design is ready.</AudienceItem>
                    <AudienceItem><strong>DSP researchers:</strong> Measure frequency, impulse, and THD responses in the offline analysis bench, visually verify signal behavior, and export plots or waveform data for further analysis.</AudienceItem>
                    <AudienceItem><strong>Computer music students:</strong> Learn functional signal processing with immediate, accessible feedback.</AudienceItem>
                </AudienceList>
            </Section>

            <Section style={{ textAlign: 'center' }}>
                <ButtonWrapper style={{ flexDirection: 'column', alignItems: 'stretch', width: 'fit-content', margin: '0 auto' }}>
                    <StyledButton
                        className="fixed-button"
                        onClick={() => window.open('https://plugins.jetbrains.com/plugin/32628', '_blank', 'noopener,noreferrer')}
                    >
                        Get on JetBrains Marketplace
                    </StyledButton>
                    <StyledButton
                        className="fixed-button"
                        href="https://gitlab.com/lperron/faust-extension"
                    >
                        View on GitLab
                    </StyledButton>
                </ButtonWrapper>
            </Section>

            <Section style={{ textAlign: 'center', marginTop: '60px' }}>
                <SectionTitle>REQUIREMENTS</SectionTitle>
                <TechSpecsList>
                    <TechSpecItem><span>IDE</span><span>Any JetBrains IDE 2023.3+ (IntelliJ, CLion, Rider, WebStorm, etc.)</span></TechSpecItem>
                    <TechSpecItem><span>OS</span><span>Windows, macOS, Linux</span></TechSpecItem>
                    <TechSpecItem><span>Local Setup</span><span>None required for live preview (WASM bundled)</span></TechSpecItem>
                    <TechSpecItem><span>Native Compile Path</span><span>Optional — auto-detected when Faust is installed; useful for very large or complex DSPs</span></TechSpecItem>
                    <TechSpecItem><span>Export Targets</span><span>Requires local Faust installation</span></TechSpecItem>
                </TechSpecsList>
            </Section>
        </ProjectWrapper>
    );
};

export default FaustStudioPage;
